import { PaginatedItin } from './../../shared/models/paginated-itin.model';
import { ItinActionService } from './../../core/itinerari/itinerari.action';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { BreakpointObserver } from '@angular/cdk/layout';
import { select } from '@angular-redux/store';
import { Observable, Subscription, of } from 'rxjs';
import { tap, first, mergeMap, startWith, switchMap, map, catchError } from 'rxjs/operators';
import { Itin } from 'src/app/core/itinerari/itinerari.model';
import { TranslateService } from '@ngx-translate/core';
import { IUser } from 'src/app/core/users/users.model';
import { ApiService } from 'src/app/services/api.service';
import { LoggedUser } from 'src/app/core/logged/logged.model';

@Component({
  selector: 'app-lista-itinerari',
  templateUrl: './lista-itinerari.component.html',
  styleUrls: ['./lista-itinerari.component.scss']
})
export class ListaItinerariComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[];
  dataSource: MatTableDataSource<Itin>;
  columnDefinitions = {
    mobile: ['itin_name'],
    desktop: ['itin_name', 'pois_list']
  };
  current_lang = 'it';

  @select(['itin', 'list'])
  itin_list$: Observable<any>;
  @select(['itin', 'total'])
  itin_list_total$: Observable<any>;
  itin_list_total: number = 0;
  @select(['itin', 'page'])
  itin_list_page$: Observable<any>;
  itin_list_page: number = 0;
  @select(['itin', 'size'])
  itin_list_size$: Observable<any>;
  itin_list_size: number = 0;
  @select(['logged', 'logged'])
  user_logged$: Observable<any>;
  user_logged: IUser;

  loading = false;
  loadingItin = false;

  private userGeoPosId: number;

  private searchString: string = '';

  private subscriptions: Subscription[] = [];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private titleService: Title,
    private breakpointObserver: BreakpointObserver,
    private translate: TranslateService,
    private itinAction: ItinActionService,
    private api: ApiService
    ) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.admin_itin').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe(() => {
      this.translate.get('breadcrumb.admin_itin').subscribe((res: string) => {
        this.titleService.setTitle(res + ' - PRIMIS');
      });
      this.changeLangFunction();
    });
  }

  ngOnInit(): void {
    this.user_logged$.pipe(
      first(),
      tap((user: LoggedUser) => {
        this.userGeoPosId = user.auth_user === 'superadmin' ? null : user.geopos[0].id;
      })).subscribe();

    this.subscriptions.push(
      this.itin_list_total$.subscribe((total) => {
        this.itin_list_total = total;
      }),
      this.itin_list_size$.subscribe((size) => {
        this.itin_list_size = size;
      }),
      this.breakpointObserver.observe('(max-width: 800px)').subscribe(result => {
        if (result.matches) {
          this.displayedColumns = this.columnDefinitions.mobile;
        } else {
          this.displayedColumns = this.columnDefinitions.desktop;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.paginator.page.pipe(
        startWith({}),
        switchMap(() => {
          const pageIndex = !!this.paginator.pageIndex ? this.paginator.pageIndex : 0;
          this.loading = true;
          return this.api.getItin(
            pageIndex,
            this.userGeoPosId,
            null,
            this.searchString,
            this.current_lang,
            true
          );
        }),
        map((paginatedItin: PaginatedItin) => {
          this.dataSource = new MatTableDataSource(paginatedItin.list);
          this.itinAction.paginatedItinList(paginatedItin);
          this.loading = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di itinerari');
          this.loading = false;
          return of(err);
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  applyFilter(event: Event) {
    const searchItinLength = this.searchString.length;
    this.searchString = this.getSearchString((event.target as HTMLInputElement).value);
    if (searchItinLength !== this.searchString.length) {
      this.paginator.firstPage();
      this.loadingItin = true;
      this.api.getItin(
        0,
        this.userGeoPosId,
        null,
        this.searchString,
        this.current_lang,
        true
      ).pipe(
        first(),
        tap((paginatedItin: PaginatedItin) => {
          this.dataSource = new MatTableDataSource(paginatedItin.list);
          this.itinAction.paginatedItinList(paginatedItin);
          this.loadingItin = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di itinerari');
          this.loadingItin = false;
          return of(err);
        })
      ).subscribe();
    }
  }

  private getSearchString(searchString: string): string {
    if (!searchString || searchString?.length < 4) {
      return '';
    }
    return searchString;
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.translate.get('admin_itin.paginator').subscribe(translation => {
      this.paginator._intl.itemsPerPageLabel = translation.items_per_page;
      this.paginator._intl.nextPageLabel = translation.next;
      this.paginator._intl.previousPageLabel = translation.prev;
      this.paginator._intl.getRangeLabel = function(page, pageSize, length) {
        const of = translation.elem_of_tot;
        if (length === 0 || pageSize === 0) {
          return '0 ' + of + ' ' + length;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        // If the start index exceeds the list length, do not try and fix the end index to the end.
        const endIndex = startIndex < length ?
          Math.min(startIndex + pageSize, length) :
          startIndex + pageSize;
        return startIndex + 1 + ' - ' + endIndex + ' ' + of + ' ' + length;
      };
    });
  }

  listOfPois(single_itin) {
    let supp_array = [];
    for (const itin of single_itin.pois) {
      supp_array.push(itin.poi_name[this.current_lang]);
    }
    return supp_array.join(', ');
  }

}
